import { useCallback, useRef, useState } from "react";
import _debounce from "lodash/debounce";

/**
 * useCounterPill
 *
 * @returns {object} - object containing the following properties:
 *
 * @property {React.RefObject<HTMLDivElement>} valueContainerChildRef - ref to the value container child
 * @property {function} resetHiddenPillLabels - function to reset the hidden pill labels
 * @property {function} renderCounterPill - function to render the counter pill
 * @property {string[]} hiddenPillLabels - array of hidden pill labels
 *
 * @example
 *
 * const {
 *  valueContainerChildRef,
 *  resetHiddenPillLabels,
 *  renderCounterPill,
 *  hiddenPillLabels,
 * } = useCounterPill();
 *
 * return (
 *  <ValueContainer
 *    ref={valueContainerChildRef}
 *    {...props}
 *  >
 *    {props.children}
 *    {hiddenPillLabels.length > 0 && (
 *      <SelectedHiddenCountIndicator
 *        onClick={renderCounterPill}
 *      >
 *        {hiddenPillLabels.length}
 *      </SelectedHiddenCountIndicator>
 *  )}
 *  </ValueContainer>
 * );
 *
 **/
export const useCounterPill = () => {
  /**
   * This ref is used to get the children of the ValueContainer component.
   * We need to get the children to calculate the hidden pills.
   * We need to use a ref because the children are not available in the
   * first render.
   **/
  const pillContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Shows the labels as Pills inside the GroupPill inner dropdown.
   **/
  const [hiddenPillLabels, setHiddenPillLabels] = useState<string[]>([]);

  /**
   * This function gets the hidden pills.
   * It gets the children of the StyledInnerContainer component and calculates
   * the hidden pills that are wrapped to the next line in the flex container,
   * based on the bottom position of the first child and returning the children
   * that have a bottom position greater than the first child, meaning that
   * they are wrapped to the next line.
   **/
  const getHiddenChildren = useCallback(() => {
    if (!pillContainerRef.current) return [];
    const children = pillContainerRef.current?.children ?? [];

    // The last child is the input caret. We don't want to count it.
    const childrenWithoutCaret = Array.from(children).slice(0, -1);

    if (!childrenWithoutCaret.length) return [];

    const firstChildBottom =
      childrenWithoutCaret?.[0].getBoundingClientRect().bottom;

    const hiddenChildren = childrenWithoutCaret.filter(
      (child) => child.getBoundingClientRect().bottom > firstChildBottom
    );

    return hiddenChildren;
  }, []);

  const getLabelsFromHiddenChildren = useCallback(
    (hiddenChildren: Element[]) =>
      hiddenChildren.map((child) => child.textContent ?? ""),
    []
  );

  const refreshHiddenLabels = useCallback(() => {
    if (pillContainerRef.current) {
      const hiddenPills = getHiddenChildren();
      const labels = getLabelsFromHiddenChildren(hiddenPills);
      setHiddenPillLabels(labels);
    }
  }, [getHiddenChildren, getLabelsFromHiddenChildren]);

  const debouncedRefreshHiddenLabels = _debounce(refreshHiddenLabels, 50);

  const resetHiddenPillLabels = useCallback(() => setHiddenPillLabels([]), []);

  return {
    pillContainerRef,
    resetHiddenPillLabels,
    renderPillCounter: debouncedRefreshHiddenLabels,
    hiddenPillLabels,
  };
};
